import { computeItemTotal, sumCartItems } from "@/lib/pricing"
import { findCategoryForOffer } from "@/lib/find-category-for-offer"
import {
  explainOfferUnavailability,
  type OfferUnavailabilityReason,
} from "@/lib/is-offer-available-now"
import { isOptionAvailable } from "@/lib/is-option-available"
import { findInvalidOptionGroup } from "@/lib/option-group-selection"
import type {
  Availability,
  Cart,
  CartItem,
  Category,
  Item,
  ItemOffer,
  OptionGroup,
} from "@/types"

/**
 * The catalog slices needed to revalidate a cart. Structurally satisfied by the
 * whole `ApiData` returned from a fresh `fetchApi`, so callers can pass it
 * directly.
 */
export interface CatalogSnapshot {
  itemOffers: ItemOffer[]
  optionGroups: OptionGroup[]
  categories: Category[]
  availabilities: Availability[]
  items: Item[]
}

/** Why a cart line is not orderable against the fresh catalog. */
export type LineUnavailabilityReason =
  | OfferUnavailabilityReason
  | "offer_missing"
  | "offer_not_in_menu"
  | "option_missing"
  | "option_paused"
  | "option_group_missing"
  | "option_group_unsatisfied"

/** Reasons tied to the clock, not to stock: a scheduled order may still accept the line. */
export const SCHEDULE_WINDOW_REASONS: ReadonlySet<LineUnavailabilityReason> = new Set([
  "offer_out_of_schedule",
  "category_out_of_schedule",
])

/** One cart line as the fresh catalog sees it; `reason` is null when the line is still orderable. */
export interface LineRevalidation {
  lineId: string
  offerId: string
  name: string
  reason: LineUnavailabilityReason | null
  /** The option behind an `option_*` reason. */
  optionId?: string
  /** Display name of that option (from the bag), when known. */
  optionName?: string
  /** The group behind `option_group_*` reasons. */
  optionGroupId?: string
  /** Display name of that group (from the bag), when known. */
  optionGroupName?: string
  previousTotal: number
  refreshedTotal: number
  freshOffer: Pick<ItemOffer, "status" | "availabilityId" | "price"> | null
}

export interface CatalogRevalidationResult {
  /**
   * Cart lines whose offer is gone from the catalog or no longer available now
   * (status or scheduled hours), plus the lines carrying an option the merchant
   * has since paused. Empty when every line is still orderable.
   */
  unavailableItems: CartItem[]
  /** Per-line verdict with the reason and the totals before/after repricing. */
  lines: LineRevalidation[]
  /**
   * True when at least one still-available line's total moved by 1 cent or
   * more against the fresh catalog. The caller should restamp the bag and
   * stop the submit so a stale unit price never becomes an order.
   */
  pricesMoved: boolean
  /**
   * The cart with every line's `unitPrice`/`itemTotalPrice` re-derived against
   * the fresh offer + option prices (selection kept intact) and the total
   * recomputed from the lines.
   */
  refreshedCart: Cart
}

function cents(value: number): number {
  return Math.round((Number(value) || 0) * 100)
}

/**
 * Revalidate a cart against a freshly fetched catalog right before checkout.
 *
 * The 60s catalog poll can leave the cart holding a stale availability status,
 * so a line pulled from the menu would be submitted and rejected post-accept.
 * This mirrors, on the client, the merchant's item-availability check.
 *
 * Price drift is reported via `pricesMoved` so the caller can restamp the bag
 * and abort the POST. Submitting the frozen cart unit price is what lets a
 * catalog change (e.g. 34,90 → 39,90) leave as a second, fake total.
 *
 * A line whose selection no longer satisfies every option group the fresh
 * offer exposes (a mandatory group added after the line was built, a widened
 * minimum) is reported as `option_group_unsatisfied`, the same bar the reorder
 * applies and the one the merchant enforces at checkout (ORD-CHK-1008/1009).
 *
 * Pure: no side effects. The caller decides whether to block the submit and how
 * to surface the outcome.
 */
export function revalidateCartAgainstCatalog(
  cart: Cart,
  catalog: CatalogSnapshot,
): CatalogRevalidationResult {
  const { itemOffers, optionGroups, categories, availabilities, items } = catalog

  const offerById = new Map(itemOffers.map((offer) => [offer.id, offer]))
  const groupById = new Map(optionGroups.map((group) => [group.id, group]))
  const itemById = new Map((items ?? []).map((item) => [item.id, item]))

  function optionDisplayName(cartItem: CartItem, optionId: string): string | undefined {
    const fromBag = cartItem.itemOptions?.[optionId]?.name
    if (fromBag) return fromBag
    for (const group of cartItem.optionGroups) {
      const option = group.options?.find((op) => op.id === optionId)
      if (option) {
        const fromCatalog = itemById.get(option.itemId)?.name
        if (fromCatalog) return fromCatalog
        const fromBagByItem = cartItem.itemOptions?.[option.itemId]?.name
        if (fromBagByItem) return fromBagByItem
      }
    }
    return undefined
  }

  function findPausedSelection(cartItem: CartItem): {
    optionId?: string
    optionName?: string
    optionGroupId?: string
    optionGroupName?: string
    reason: "option_missing" | "option_paused" | "option_group_missing"
  } | null {
    for (const group of cartItem.optionGroups) {
      const selection = cartItem.optionQuantities?.[group.id] ?? {}
      const hasSelection = Object.values(selection).some((quantity) => !!quantity)
      const freshGroup = groupById.get(group.id)
      if (!freshGroup) {
        if (!hasSelection) continue
        return {
          optionGroupId: group.id,
          optionGroupName: group.name,
          reason: "option_group_missing",
        }
      }
      for (const [optionId, quantity] of Object.entries(selection)) {
        if (!quantity) continue
        const option = freshGroup.options.find((op) => op.id === optionId)
        if (!option) {
          return {
            optionId,
            optionName: optionDisplayName(cartItem, optionId),
            optionGroupId: group.id,
            optionGroupName: group.name,
            reason: "option_missing",
          }
        }
        if (!isOptionAvailable(option, itemById.get(option.itemId))) {
          return {
            optionId,
            optionName: optionDisplayName(cartItem, optionId),
            optionGroupId: group.id,
            optionGroupName: group.name,
            reason: "option_paused",
          }
        }
      }
    }
    return null
  }

  const unavailableItems: CartItem[] = []
  const lines: LineRevalidation[] = []

  const refreshedItems = cart.items.map((cartItem) => {
    const freshOffer = offerById.get(cartItem.itemOffer.id)
    const line: LineRevalidation = {
      lineId: cartItem.id,
      offerId: cartItem.itemOffer.id,
      name: cartItem.item.name,
      reason: null,
      previousTotal: cartItem.itemTotalPrice,
      refreshedTotal: cartItem.itemTotalPrice,
      freshOffer: null,
    }
    lines.push(line)

    if (!freshOffer) {
      line.reason = "offer_missing"
      unavailableItems.push(cartItem)
      return cartItem
    }
    line.freshOffer = {
      status: freshOffer.status,
      availabilityId: freshOffer.availabilityId,
      price: freshOffer.price,
    }

    const category = findCategoryForOffer(categories, freshOffer.id)
    if (!category) {
      line.reason = "offer_not_in_menu"
      unavailableItems.push(cartItem)
      return cartItem
    }

    const offerReason = explainOfferUnavailability(freshOffer, availabilities, category)
    const paused = offerReason ? null : findPausedSelection(cartItem)
    const exposedGroups = (freshOffer.optionGroupsId ?? [])
      .map((groupId) => groupById.get(groupId))
      .filter((group): group is OptionGroup => group !== undefined && group.status === "AVAILABLE")
    const unsatisfied =
      offerReason || paused ? null : findInvalidOptionGroup(exposedGroups, cartItem.optionQuantities ?? {})
    const reason = offerReason ?? paused?.reason ?? (unsatisfied ? "option_group_unsatisfied" : null)
    if (reason) {
      line.reason = reason
      if (paused) {
        line.optionId = paused.optionId
        line.optionName = paused.optionName
        line.optionGroupId = paused.optionGroupId
        line.optionGroupName = paused.optionGroupName
      }
      if (unsatisfied) {
        line.optionGroupId = unsatisfied.id
        line.optionGroupName =
          cartItem.optionGroups.find((group) => group.id === unsatisfied.id)?.name ?? unsatisfied.name
      }
      unavailableItems.push(cartItem)
    }

    const freshGroups = cartItem.optionGroups.map(
      (group) => groupById.get(group.id) ?? group,
    )
    const unitPrice = computeItemTotal(freshOffer, freshGroups, cartItem.optionQuantities)
    line.refreshedTotal = unitPrice * cartItem.quantity

    return {
      ...cartItem,
      itemOffer: freshOffer,
      optionGroups: freshGroups,
      unitPrice,
      itemTotalPrice: line.refreshedTotal,
    }
  })

  const refreshedTotal = sumCartItems(refreshedItems)

  const refreshedCart: Cart = {
    ...cart,
    items: refreshedItems,
    totalPrice: refreshedTotal,
  }

  const pricesMoved = refreshedItems.some((item, index) => {
    const previous = cart.items[index]
    return cents(item.itemTotalPrice) !== cents(previous?.itemTotalPrice ?? 0)
  })

  return { unavailableItems, lines, pricesMoved, refreshedCart }
}
